import React, { useEffect, useState, useCallback } from 'react';
import { receiptsAPI } from '../../services/garageApi';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    Eye,
    Trash2,
    DollarSign,
    CreditCard,
    FileText,
    Banknote,
    Receipt,
    AlertCircle,
    Loader,
    X
} from 'lucide-react';

const Receipts = () => {
    const navigate = useNavigate();

    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 10;
    const [filters, setFilters] = useState({
        type: '',
        paymentMethod: '',
        dateFrom: '',
        dateTo: '',
    });

    const fetchReceipts = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {
                ...filters,
                page: currentPage,
                per_page: itemsPerPage
            };
            const res = await receiptsAPI.getAll(params);
            const list = res.data || [];
            setReceipts(Array.isArray(list) ? list : []);

            if (res.pagination) {
                setTotalPages(res.pagination.last_page || 1);
                setTotalItems(res.pagination.total || list.length);
            } else {
                setTotalPages(1);
                setTotalItems(list.length);
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Failed to load receipts');
            setReceipts([]);
        } finally {
            setLoading(false);
        }
    }, [filters, currentPage]);

    useEffect(() => {
        fetchReceipts();
    }, [fetchReceipts]);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this receipt?')) return;
        try {
            await receiptsAPI.delete(id);
            fetchReceipts();
        } catch (err) {
            console.error(err);
            alert('Error deleting receipt. Please try again.');
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({ type: '', paymentMethod: '', dateFrom: '', dateTo: '' });
        setCurrentPage(1);
    };

    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const goToPage = (page) => setCurrentPage(page);
    const goToNext = () => { if (currentPage < totalPages) setCurrentPage(prev => prev + 1); };
    const goToPrevious = () => { if (currentPage > 1) setCurrentPage(prev => prev - 1); };
    const hasNext = currentPage < totalPages;
    const hasPrevious = currentPage > 1;

    const getTypeBadge = (type) => {
        const config = {
            sale: { color: 'bg-green-100 text-green-800', label: 'Sale' },
            purchase: { color: 'bg-blue-100 text-blue-800', label: 'Purchase' },
        };
        return config[type] || { color: 'bg-gray-100 text-gray-800', label: type || 'Unknown' };
    };

    const getPaymentMethodIcon = (method) => {
        switch (method) {
            case 'cash': return <Banknote className="h-4 w-4 text-green-600" />;
            case 'cheque': return <FileText className="h-4 w-4 text-blue-600" />;
            case 'card':
            case 'credit_card': return <CreditCard className="h-4 w-4 text-purple-600" />;
            case 'bank_transfer': return <CreditCard className="h-4 w-4 text-indigo-600" />;
            default: return <DollarSign className="h-4 w-4 text-gray-600" />;
        }
    };

    const getPaymentMethodLabel = (method) => {
        const labels = {
            cash: 'Cash',
            cheque: 'Cheque',
            card: 'Card',
            credit_card: 'Credit Card',
            bank_transfer: 'Bank Transfer',
        };
        return labels[method] || method || '-';
    };

    // Summary calculations
    const totalSalePayments = receipts
        .filter(r => r.type === 'sale')
        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    const totalPurchasePayments = receipts
        .filter(r => r.type === 'purchase')
        .reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    const totalAmount = receipts.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="flex items-center">
                <Loader className="animate-spin h-5 w-5 mr-2" />
                Loading receipts...
            </div>
        </div>
    );

    if (error) return (
        <div className="space-y-4">
            <div className="rounded-md bg-red-50 border border-red-200 p-4">
                <div className="flex items-center space-x-2 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4" />
                    <span>{error}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Receipt className="h-6 w-6 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900">Receipts</h1>
                </div>
                <button
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    onClick={() => navigate('/receipts/create')}
                >
                    <Plus className="h-4 w-4 mr-2" />
                    New Receipt
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-blue-100 rounded-full">
                            <Receipt className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Total Receipts</p>
                            <p className="text-xl font-bold text-gray-900">{receipts.length}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-green-100 rounded-full">
                            <DollarSign className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Sale Payments</p>
                            <p className="text-xl font-bold text-green-600">${totalSalePayments.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-blue-100 rounded-full">
                            <DollarSign className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Purchase Payments</p>
                            <p className="text-xl font-bold text-blue-600">${totalPurchasePayments.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0 p-3 bg-purple-100 rounded-full">
                            <DollarSign className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm text-gray-500">Total Amount</p>
                            <p className="text-xl font-bold text-purple-600">${totalAmount.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white shadow rounded-lg">
                <div className="p-4 border-b border-gray-200">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                        <select
                            name="type"
                            value={filters.type}
                            onChange={handleFilterChange}
                            className="form-select rounded-md border-gray-300"
                        >
                            <option value="">All Types</option>
                            <option value="sale">Sale</option>
                            <option value="purchase">Purchase</option>
                        </select>
                        <select
                            name="paymentMethod"
                            value={filters.paymentMethod}
                            onChange={handleFilterChange}
                            className="form-select rounded-md border-gray-300"
                        >
                            <option value="">All Methods</option>
                            <option value="cash">Cash</option>
                            <option value="cheque">Cheque</option>
                            <option value="card">Card</option>
                            <option value="bank_transfer">Bank Transfer</option>
                        </select>
                        <input
                            type="date"
                            name="dateFrom"
                            value={filters.dateFrom}
                            onChange={handleFilterChange}
                            className="form-input rounded-md border-gray-300"
                            placeholder="From date"
                        />
                        <input
                            type="date"
                            name="dateTo"
                            value={filters.dateTo}
                            onChange={handleFilterChange}
                            className="form-input rounded-md border-gray-300"
                            placeholder="To date"
                        />
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <X className="h-4 w-4 mr-1" />
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Receipt #</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Related To</th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Method</th>
                                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {receipts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                                        <Receipt className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                                        <p>No receipts found</p>
                                    </td>
                                </tr>
                            ) : (
                                receipts.map((receipt) => {
                                    const badge = getTypeBadge(receipt.type);
                                    return (
                                        <tr key={receipt.id} className="hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <span className="text-sm font-medium text-gray-900">{receipt.id}</span>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                {receipt.receipt_date ? new Date(receipt.receipt_date).toLocaleDateString() : '-'}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${badge.color}`}>
                                                    {badge.label}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                <button
                                                    onClick={() => {
                                                        if (receipt.type === 'sale') {
                                                            navigate(`/sales/${receipt.receiptable_id}`);
                                                        } else if (receipt.type === 'purchase') {
                                                            navigate(`/purchases/${receipt.receiptable_id}`);
                                                        }
                                                    }}
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    {receipt.type === 'sale' ? 'Sale' : receipt.type === 'purchase' ? 'Purchase' : receipt.receiptable_type} #{receipt.receiptable_id}
                                                </button>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                                                ${Number(receipt.amount || 0).toFixed(2)}
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                                <div className="flex items-center space-x-2">
                                                    {getPaymentMethodIcon(receipt.payment_method)}
                                                    <span>{getPaymentMethodLabel(receipt.payment_method)}</span>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-center">
                                                <div className="flex items-center justify-center space-x-2">
                                                    <button
                                                        className="text-blue-600 hover:text-blue-900 p-1"
                                                        onClick={() => navigate(`/receipts/${receipt.id}`)}
                                                        title="View"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        className="text-red-600 hover:text-red-900 p-1"
                                                        onClick={() => handleDelete(receipt.id)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={goToPrevious}
                                disabled={!hasPrevious}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={goToNext}
                                disabled={!hasNext}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                                    <span className="font-medium">{totalItems}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={goToPrevious}
                                        disabled={!hasPrevious}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => goToPage(page)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                currentPage === page
                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        onClick={goToNext}
                                        disabled={!hasNext}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Receipts;
